package scenes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Slider
import androidx.compose.material3.SliderDefaults
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.*
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import domain.PlayGameMode
import engine.model.*
import kotlinx.coroutines.delay
import rendering.AnimatedBackground
import rendering.fontMono

private val COLOR_ACCENT = Color(0xFF00E676)
private val COLOR_LABEL = Color(0xFF888888)
private val COLOR_VALUE = Color(0xFFFFFFFF)
private val COLOR_SECTION = Color(0xFF444466)
private val COLOR_BORDER = Color(0xFF333355)
private val COLOR_TAB_ACTIVE = Color(0xFF00E676)
private val COLOR_TAB_INACTIVE = Color(0xFF333355)

private enum class CustomTab { GAME, OBJECTIVE, SURVIVAL }

@Composable
fun CustomGameScene(navigate: Navigate) {
    val focusRequester = remember { FocusRequester() }
    val scrollState = rememberScrollState()
    var activeTab by remember { mutableStateOf(CustomTab.GAME) }

    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)

    // GAME tab state
    var spinMode by remember { mutableStateOf(SpinMode.PIECE_DEFINED) }
    var holdEnabled by remember { mutableStateOf(true) }
    var rotation180 by remember { mutableStateOf(false) }
    var previewSize by remember { mutableStateOf(5f) }
    var entryDelay by remember { mutableStateOf(0f) }
    var lineClearDelay by remember { mutableStateOf(0f) }
    var lockDelay by remember { mutableStateOf(500f) }
    var maxLockResets by remember { mutableStateOf(15f) }

    // OBJECTIVE tab state
    var goalType by remember { mutableStateOf(GameGoal.LINES) }
    var goalValue by remember { mutableStateOf(40f) }

    // SURVIVAL tab state
    var garbageMessiness by remember { mutableStateOf(100f) }
    var garbageCap by remember { mutableStateOf(0f) }

    fun launchGame() {
        val matchConfig = MatchConfig(
            gameplay = GameplayConfig(
                spinDetection = spinMode,
                isHoldEnabled = holdEnabled,
                is180Enabled = rotation180,
                previewSize = previewSize.toInt(),
                entryDelay = entryDelay.toDouble(),
                lineClearDelay = lineClearDelay.toDouble(),
            ),
            gravity = GravityConfig(
                lockDelay = lockDelay.toDouble(),
                maxLockResets = maxLockResets.toInt(),
            ),
            objective = ObjectiveConfig(
                goalType = goalType,
                goalValue = goalValue.toDouble(),
            ),
            garbage = GarbageConfig(
                messiness = garbageMessiness.toInt(),
                cap = garbageCap.toInt(),
            ),
        )
        navigate(SceneName.GAME, NavigateOptions(mode = PlayGameMode.CUSTOM, matchConfig = matchConfig))
    }

    LaunchedEffect(Unit) {
        delay(50)
        focusRequester.requestFocus()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                when (event.key) {
                    Key.Escape -> { navigate(SceneName.PLAY, null); true }
                    Key.Tab, Key.DirectionRight -> {
                        activeTab = CustomTab.entries[(activeTab.ordinal + 1) % CustomTab.entries.size]
                        true
                    }
                    Key.DirectionLeft -> {
                        activeTab = CustomTab.entries[(activeTab.ordinal - 1 + CustomTab.entries.size) % CustomTab.entries.size]
                        true
                    }
                    Key.Enter -> { launchGame(); true }
                    else -> false
                }
            },
    ) {
        AnimatedBackground(modifier = Modifier.fillMaxSize())

        Column(modifier = Modifier.fillMaxSize()) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "CUSTOM GAME",
                    style = monoStyle.copy(fontSize = 20.sp, color = COLOR_ACCENT, letterSpacing = 0.15.sp),
                )
                Text(
                    text = "Esc to go back  •  Enter to launch",
                    style = monoStyle.copy(fontSize = 9.sp, color = COLOR_LABEL),
                )
            }

            // Tab strip
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                CustomTab.entries.forEach { tab ->
                    val isActive = tab == activeTab
                    Box(
                        modifier = Modifier
                            .clickable { activeTab = tab }
                            .border(
                                width = if (isActive) 2.dp else 1.dp,
                                color = if (isActive) COLOR_TAB_ACTIVE else COLOR_TAB_INACTIVE,
                            )
                            .padding(horizontal = 16.dp, vertical = 8.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = tab.name,
                            style = monoStyle.copy(
                                fontSize = 11.sp,
                                color = if (isActive) COLOR_TAB_ACTIVE else COLOR_LABEL,
                            ),
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // Tab content
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(scrollState)
                    .padding(horizontal = 24.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                when (activeTab) {
                    CustomTab.GAME -> {
                        // Spin detection segment
                        CgSectionHeader("SPIN DETECTION")
                        CgSegmentRow(
                            options = listOf("NONE", "PIECE", "ALL", "MINI+"),
                            selected = when (spinMode) {
                                SpinMode.NONE -> 0
                                SpinMode.PIECE_DEFINED -> 1
                                SpinMode.ALL -> 2
                                else -> 3
                            },
                            onSelect = { idx ->
                                spinMode = when (idx) {
                                    0 -> SpinMode.NONE
                                    1 -> SpinMode.PIECE_DEFINED
                                    2 -> SpinMode.ALL
                                    else -> SpinMode.ALL_MINI_PLUS
                                }
                            },
                        )

                        CgToggleRow("HOLD ENABLED", holdEnabled) { holdEnabled = it }
                        CgToggleRow("180° ROTATION", rotation180) { rotation180 = it }

                        CgSectionHeader("TIMING")
                        CgSliderRow("PREVIEW", previewSize, 1f..7f, "${previewSize.toInt()}") { previewSize = it }
                        CgSliderRow("ENTRY DELAY", entryDelay, 0f..500f, "${entryDelay.toInt()} ms") { entryDelay = it }
                        CgSliderRow("LINE CLEAR DELAY", lineClearDelay, 0f..500f, "${lineClearDelay.toInt()} ms") { lineClearDelay = it }
                        CgSliderRow("LOCK DELAY", lockDelay, 100f..1000f, "${lockDelay.toInt()} ms") { lockDelay = it }
                        CgSliderRow("MAX LOCK RESETS", maxLockResets, 0f..30f, "${maxLockResets.toInt()}") { maxLockResets = it }
                    }

                    CustomTab.OBJECTIVE -> {
                        CgSectionHeader("GOAL TYPE")
                        CgSegmentRow(
                            options = listOf("NONE", "LINES", "TIME"),
                            selected = when (goalType) {
                                GameGoal.NONE -> 0
                                GameGoal.LINES -> 1
                                GameGoal.TIME -> 2
                            },
                            onSelect = { idx ->
                                goalType = when (idx) {
                                    0 -> GameGoal.NONE
                                    1 -> GameGoal.LINES
                                    else -> GameGoal.TIME
                                }
                                // Reset goal value to sensible default
                                goalValue = if (goalType == GameGoal.TIME) 120f else 40f
                            },
                        )

                        if (goalType != GameGoal.NONE) {
                            CgSectionHeader("GOAL VALUE")
                            val (range, suffix) = if (goalType == GameGoal.TIME)
                                (30f..600f) to " s"
                            else
                                (10f..200f) to " lines"
                            CgSliderRow(
                                label = if (goalType == GameGoal.TIME) "TARGET TIME" else "TARGET LINES",
                                value = goalValue,
                                valueRange = range,
                                displayValue = "${goalValue.toInt()}$suffix",
                                onValueChange = { goalValue = it },
                            )
                        }
                    }

                    CustomTab.SURVIVAL -> {
                        CgSectionHeader("GARBAGE")
                        CgSliderRow("MESSINESS", garbageMessiness, 0f..100f, "${garbageMessiness.toInt()}%") { garbageMessiness = it }
                        CgSliderRow("CAP", garbageCap, 0f..20f, if (garbageCap == 0f) "OFF" else "${garbageCap.toInt()}") { garbageCap = it }
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Launch button
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { launchGame() }
                        .border(width = 2.dp, color = COLOR_ACCENT)
                        .padding(vertical = 14.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = "LAUNCH GAME  [Enter]",
                        style = monoStyle.copy(fontSize = 14.sp, color = COLOR_ACCENT, letterSpacing = 0.1.sp),
                        textAlign = TextAlign.Center,
                    )
                }

                Spacer(modifier = Modifier.height(32.dp))
            }
        }
    }
}

// ── Helper composables ────────────────────────────────────────────────────────

@Composable
private fun CgSectionHeader(title: String) {
    Spacer(modifier = Modifier.height(8.dp))
    Text(
        text = title,
        style = TextStyle(
            fontFamily = fontMono(),
            fontSize = 11.sp,
            color = Color(0xFF444466),
            letterSpacing = 0.2.sp,
        ),
    )
    Spacer(modifier = Modifier.height(4.dp))
}

@Composable
private fun CgSliderRow(
    label: String,
    value: Float,
    valueRange: ClosedFloatingPointRange<Float>,
    displayValue: String,
    onValueChange: (Float) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = label,
            style = TextStyle(fontFamily = fontMono(), fontSize = 10.sp, color = Color(0xFF888888)),
            modifier = Modifier.width(120.dp),
        )
        Slider(
            value = value,
            onValueChange = onValueChange,
            valueRange = valueRange,
            modifier = Modifier.weight(1f),
            colors = SliderDefaults.colors(
                thumbColor = Color(0xFF00E676),
                activeTrackColor = Color(0xFF00E676),
                inactiveTrackColor = Color(0xFF333355),
            ),
        )
        Text(
            text = displayValue,
            style = TextStyle(fontFamily = fontMono(), fontSize = 10.sp, color = Color(0xFFFFFFFF)),
            modifier = Modifier.width(64.dp),
            textAlign = TextAlign.End,
        )
    }
}

@Composable
private fun CgToggleRow(label: String, checked: Boolean, onCheckedChange: (Boolean) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = label,
            style = TextStyle(fontFamily = fontMono(), fontSize = 10.sp, color = Color(0xFF888888)),
            modifier = Modifier.weight(1f),
        )
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color(0xFF00E676),
                checkedTrackColor = Color(0xFF00E676).copy(alpha = 0.4f),
                uncheckedThumbColor = Color(0xFF888888),
                uncheckedTrackColor = Color(0xFF333355),
            ),
        )
    }
}

@Composable
private fun CgSegmentRow(options: List<String>, selected: Int, onSelect: (Int) -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        options.forEachIndexed { index, label ->
            val isSelected = index == selected
            Box(
                modifier = Modifier
                    .weight(1f)
                    .border(
                        width = if (isSelected) 2.dp else 1.dp,
                        color = if (isSelected) Color(0xFF00E676) else Color(0xFF333355),
                    )
                    .padding(vertical = 8.dp),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = label,
                    style = TextStyle(
                        fontFamily = fontMono(),
                        fontSize = 10.sp,
                        color = if (isSelected) Color(0xFF00E676) else Color(0xFF666688),
                    ),
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}
